// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CypressFields } from '../../utils/Cypress';
import { IProductCartItem } from '../../types/Cart';
import { useCart } from '../../providers/Cart.provider';
import ProductPrice from '../ProductPrice';
import * as S from './CartDropdown.styled';

interface IProps {
  isOpen: boolean;
  onClose(): void;
  productList: IProductCartItem[];
}

const CartDropdown = ({ productList, isOpen, onClose }: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { removeItem, emptyCart, setItemQuantity, isCartBusy, cartError } = useCart();

  const runCartAction = (action: () => Promise<void>) => {
    action().catch(() => undefined);
  };

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Element;
      if (target.closest?.('[data-cart-toggle]')) {
        return;
      }
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [ref, onClose]);

  const itemCount = productList.reduce((count, { quantity }) => count + quantity, 0);
  const subtotalNanos = productList.reduce(
    (total, { quantity, product: { priceUsd = { units: 0, nanos: 0, currencyCode: 'USD' } } }) =>
      total + (Number(priceUsd.units) * 1e9 + Number(priceUsd.nanos)) * quantity,
    0
  );
  const subtotal = {
    currencyCode: productList[0]?.product.priceUsd?.currencyCode || 'USD',
    units: Math.floor(subtotalNanos / 1e9),
    nanos: subtotalNanos % 1e9,
  };

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      <S.Overlay />
      <S.CartDropdown ref={ref} data-cy={CypressFields.CartDropdown} role="dialog" aria-label="Shopping cart">
        <S.Header>
          <S.Title>
            Your cart
            {itemCount > 0 && <span>({itemCount})</span>}
          </S.Title>
          <S.HeaderActions>
            {productList.length > 0 && (
              <S.RemoveAllButton
                onClick={() => runCartAction(emptyCart)}
                disabled={isCartBusy}
                aria-label="Remove all items"
              >
                Remove all
              </S.RemoveAllButton>
            )}
            <S.CloseButton onClick={onClose} aria-label="Close shopping cart">
              ×
            </S.CloseButton>
          </S.HeaderActions>
        </S.Header>
        <S.ItemList>
          {!productList.length && (
            <S.EmptyState>
              <S.EmptyIcon aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 7h12l1 14H5L6 7z" />
                  <path d="M9 10V6a3 3 0 0 1 6 0v4" />
                </svg>
              </S.EmptyIcon>
              <S.EmptyCart>Your cart is empty</S.EmptyCart>
              <S.EmptyHint>The night sky is waiting.</S.EmptyHint>
              <S.StartShoppingLink href="/#shop" onClick={onClose}>
                Start shopping
              </S.StartShoppingLink>
            </S.EmptyState>
          )}
          {productList.map(
            ({ quantity, product: { name, picture, id, priceUsd = { nanos: 0, currencyCode: 'USD', units: 0 } } }) => (
              <S.Item key={id} data-cy={CypressFields.CartDropdownItem}>
                <S.ItemLink href={`/product/${id}`} onClick={onClose}>
                  <S.ItemImageWell>
                    <S.ItemImage src={picture} alt={name} />
                  </S.ItemImageWell>
                </S.ItemLink>
                <S.ItemBody>
                  <S.ItemNameLink href={`/product/${id}`} onClick={onClose}>
                    <S.ItemName>{name}</S.ItemName>
                  </S.ItemNameLink>
                  <ProductPrice price={priceUsd} />
                  <S.QuantityStepper aria-label={`Quantity of ${name}`}>
                    <S.StepButton
                      onClick={() => runCartAction(() => setItemQuantity(id, quantity - 1))}
                      disabled={isCartBusy}
                      aria-label={`Decrease quantity of ${name}`}
                    >
                      −
                    </S.StepButton>
                    <S.StepValue>{quantity}</S.StepValue>
                    <S.StepButton
                      onClick={() => runCartAction(() => setItemQuantity(id, quantity + 1))}
                      disabled={isCartBusy}
                      aria-label={`Increase quantity of ${name}`}
                    >
                      +
                    </S.StepButton>
                  </S.QuantityStepper>
                </S.ItemBody>
                <S.RemoveItemButton
                  onClick={() => runCartAction(() => removeItem(id))}
                  disabled={isCartBusy}
                  aria-label={`Remove ${name} from cart`}
                >
                  ×
                </S.RemoveItemButton>
              </S.Item>
            )
          )}
        </S.ItemList>
        {cartError && <S.ErrorNote role="alert">{cartError}</S.ErrorNote>}
        {productList.length > 0 && (
          <S.Footer>
            <S.SubtotalRow>
              Subtotal
              <ProductPrice price={subtotal} />
            </S.SubtotalRow>
            <S.ShippingNote>Shipping calculated at checkout.</S.ShippingNote>
            <S.CheckoutLink
              href="/cart"
              data-cy={CypressFields.CartGoToShopping}
              aria-disabled={isCartBusy}
              onClick={event => {
                if (isCartBusy) {
                  event.preventDefault();
                  return;
                }
                onClose();
              }}
            >
              Check out
            </S.CheckoutLink>
          </S.Footer>
        )}
      </S.CartDropdown>
    </>,
    document.body
  );
};

export default CartDropdown;
